import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { userCreateOrUpdate } from '../api/userApi';
import { saveUserToAlgolia } from '../services/userAlgoliaService';
import './Onboarding.css';

export default function Onboarding() {
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [skillLevel, setSkillLevel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentUser, refreshUserProfile } = useAuth();
  const { gameId } = useGame();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!yearsOfExperience || !skillLevel) {
      setError('Please fill in all fields');
      return;
    }

    // Validate years of experience
    const years = parseInt(yearsOfExperience);
    if (isNaN(years) || years < 0 || years > 50) {
      setError('Please enter a valid number of years (0-50)');
      return;
    }

    setLoading(true);

    try {
      if (!currentUser || !currentUser.uid) {
        throw new Error('User not authenticated. Please sign in again.');
      }

      const userDocRef = doc(db, 'users', currentUser.uid);
      const existingUserDoc = await getDoc(userDocRef);
      const existingData = existingUserDoc.exists() ? existingUserDoc.data() : {};
      const existingGameProfiles = existingData.gameProfiles || {};
      const gameIdToUse = gameId || 'table-tennis';

      await userCreateOrUpdate({
        email: currentUser.email,
        displayName: currentUser.displayName || currentUser.email || existingData.displayName,
        photoURL: currentUser.photoURL || existingData.photoURL || null,
        yearsOfExperience: years,
        skillLevel: skillLevel,
        gameProfiles: {
          ...existingGameProfiles,
          [gameIdToUse]: {
            ...(existingGameProfiles[gameIdToUse] || {}),
            skillLevel: skillLevel,
            yearsOfExperience: years,
          },
        },
        onboarded: true,
        createdAt: existingData.createdAt || (currentUser.metadata?.creationTime ? new Date(currentUser.metadata.creationTime) : new Date()),
        isAdmin: existingData.isAdmin || false,
      });

      const savedDoc = await getDoc(userDocRef);
      if (!savedDoc.exists()) {
        throw new Error('Failed to save user data. Please try again.');
      }

      const savedData = savedDoc.data();
      if (!savedData.yearsOfExperience || !savedData.skillLevel) {
        throw new Error('Data was not saved correctly. Please try again.');
      }

      console.log('✅ Onboarding data saved successfully:', {
        yearsOfExperience: savedData.yearsOfExperience,
        skillLevel: savedData.skillLevel,
        onboarded: savedData.onboarded
      });

      // Refresh user profile in AuthContext
      if (refreshUserProfile) {
        await refreshUserProfile();
      }

      const membersSnap = await getDocs(query(collection(db, 'groupMembers'), where('userId', '==', currentUser.uid)));
      const approvedGroupIds = membersSnap.docs.filter((d) => d.data().approved === true).map((d) => d.data().groupId);
      await saveUserToAlgolia(
        { id: currentUser.uid, displayName: savedData.displayName, email: currentUser.email || savedData.email, phone: savedData.phone },
        approvedGroupIds
      );

      // Small delay to ensure Firestore has processed the write
      await new Promise(resolve => setTimeout(resolve, 300));

      // Navigate to home feed after successful onboarding
      navigate('/');
    } catch (err) {
      console.error('❌ Error saving onboarding data:', err);
      setError(err.message || 'Failed to save your information. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <h1>Welcome to GradedPlay!</h1>
        <p className="subtitle">Tell us about yourself to get started</p>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Years of Experience</label>
            <input
              type="number"
              value={yearsOfExperience}
              onChange={(e) => setYearsOfExperience(e.target.value)}
              min="0"
              max="50"
              required
              placeholder="Enter years of experience"
            />
          </div>
          
          <div className="form-group">
            <label>Skill Level</label>
            <div className="skill-level-options">
              <button
                type="button"
                className={`skill-option ${skillLevel === 'beginner' ? 'active' : ''}`}
                onClick={() => setSkillLevel('beginner')}
              >
                Beginner
              </button>
              <button
                type="button"
                className={`skill-option ${skillLevel === 'average' ? 'active' : ''}`}
                onClick={() => setSkillLevel('average')}
              >
                Average
              </button>
              <button
                type="button"
                className={`skill-option ${skillLevel === 'advanced' ? 'active' : ''}`}
                onClick={() => setSkillLevel('advanced')}
              >
                Advanced
              </button>
            </div>
          </div>
          
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

